import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import React, { useEffect, useState } from 'react'

import {
  createPlannerTimetable,
  fetchPlannerGradeGroup,
  fetchPlannerTimetable,
  savePlannerTimetableRows,
  updatePlannerTimetable
} from 'api'
import { showToast } from 'app/helpers'

const items = (d) =>
  d && Array.isArray(d.results) ? d.results : Array.isArray(d) ? d : []

const DAYS = [
  ['mon', 'Monday'],
  ['tue', 'Tuesday'],
  ['wed', 'Wednesday'],
  ['thu', 'Thursday'],
  ['fri', 'Friday']
]

const rowsFromTemplate = (tmpl) =>
  (tmpl && tmpl.rows
    ? [...tmpl.rows].sort((a, b) => a.row_index - b.row_index)
    : []
  ).map((r) => {
    const out = { time_label: r.time_label }
    DAYS.forEach(([k]) => {
      out[k] = (r.cells && r.cells[k] && r.cells[k].label) || ''
    })
    return out
  })

/** Timetable editor — timetables are authored per GRADE and shared across every
 *  centre. A grade can have several ("Timetable 1", "Timetable 2"…), one per
 *  batch that grade can run; teachers claim one for their class from the app. */
const Timetables = () => {
  const [grades, setGrades] = useState([])
  const [gradeIdx, setGradeIdx] = useState(0)
  const [templates, setTemplates] = useState([]) // this grade's timetables
  const [selectedId, setSelectedId] = useState('')
  const [name, setName] = useState('')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const grade = grades[gradeIdx]

  useEffect(() => {
    fetchPlannerGradeGroup({})
      .then((g) => setGrades(items(g)))
      .catch(() => showToast('Failed to load grades', 'danger'))
  }, [])

  const selectTemplate = (tmpl) => {
    setSelectedId(tmpl ? tmpl.uuid : '')
    setName(tmpl ? tmpl.name : '')
    setRows(tmpl ? rowsFromTemplate(tmpl) : [])
  }

  const loadTemplates = async (keepId) => {
    if (!grade) return
    setLoading(true)
    try {
      const list = items(
        await fetchPlannerTimetable({ queries: { grade_group: grade.uuid } })
      )
      setTemplates(list)
      const keep = keepId && list.find((t) => t.uuid === keepId)
      selectTemplate(keep || list[0] || null)
    } catch (e) {
      showToast('Failed to load timetables', 'danger')
    }
    setLoading(false)
  }

  useEffect(() => {
    setTemplates([])
    setSelectedId('')
    setName('')
    setRows([])
    loadTemplates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grade && grade.uuid])

  const setCell = (i, key, value) =>
    setRows((rs) =>
      rs.map((r, idx) => (idx === i ? { ...r, [key]: value } : r))
    )

  const addRow = () =>
    setRows((rs) => [
      ...rs,
      { time_label: '', mon: '', tue: '', wed: '', thu: '', fri: '' }
    ])

  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i))

  const addTimetable = async () => {
    if (!grade) return
    setSaving(true)
    try {
      const created = await createPlannerTimetable({
        grade_group: grade.uuid,
        name: `Timetable ${templates.length + 1}`,
        display_order: templates.length
      })
      await loadTemplates(created && created.uuid)
      showToast('Timetable added', 'success')
    } catch (e) {
      showToast('Failed to add timetable', 'danger')
    }
    setSaving(false)
  }

  const saveRows = async () => {
    if (!grade || !selectedId) return
    setSaving(true)
    try {
      const current = templates.find((t) => t.uuid === selectedId)
      if (current && name.trim() && name.trim() !== current.name) {
        await updatePlannerTimetable({
          uuid: selectedId,
          grade_group: grade.uuid,
          name: name.trim(),
          display_order: current.display_order
        })
      }
      await savePlannerTimetableRows(selectedId, rows)
      await loadTemplates(selectedId)
      showToast('Timetable saved', 'success')
    } catch (e) {
      showToast('Failed to save timetable', 'danger')
    }
    setSaving(false)
  }

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>
        Class Timetables
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Timetables are per grade and shared across every centre. Add one for
        each class a grade can run (Timetable 1, 2, 3…). Teachers then pick a
        timetable for their own class.
      </Typography>

      <Tabs
        value={gradeIdx}
        onChange={(e, v) => setGradeIdx(v)}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
      >
        {grades.map((g) => (
          <Tab key={g.uuid} label={`${g.emoji || ''} ${g.name}`} />
        ))}
      </Tabs>

      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <Box
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            style={{ gap: 8 }}
            mt={2}
          >
            {templates.map((t) => (
              <Chip
                key={t.uuid}
                label={t.name}
                clickable
                color={t.uuid === selectedId ? 'primary' : 'default'}
                variant={t.uuid === selectedId ? 'default' : 'outlined'}
                onClick={() => selectTemplate(t)}
              />
            ))}
            <Button
              size="small"
              variant="outlined"
              onClick={addTimetable}
              disabled={saving}
            >
              + Add timetable
            </Button>
          </Box>

          {!templates.length ? (
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: 16 }}
            >
              No timetables yet for this grade. Add one to get started.
            </Typography>
          ) : (
            <>
              <Box mt={2} style={{ maxWidth: 320 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Timetable name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Box>

              <Paper variant="outlined" style={{ marginTop: 12 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: 120 }}>Time</TableCell>
                      {DAYS.map(([, label]) => (
                        <TableCell key={label}>{label}</TableCell>
                      ))}
                      <TableCell style={{ width: 40 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <TextField
                            size="small"
                            value={r.time_label}
                            onChange={(e) =>
                              setCell(i, 'time_label', e.target.value)
                            }
                          />
                        </TableCell>
                        {DAYS.map(([k]) => (
                          <TableCell key={k}>
                            <TextField
                              size="small"
                              fullWidth
                              value={r[k]}
                              onChange={(e) => setCell(i, k, e.target.value)}
                            />
                          </TableCell>
                        ))}
                        <TableCell>
                          <IconButton size="small" onClick={() => removeRow(i)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>

              <Box mt={2} display="flex" style={{ gap: 12 }}>
                <Button variant="outlined" onClick={addRow}>
                  + Add row
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={saveRows}
                  disabled={saving || !selectedId}
                >
                  {saving ? 'Saving…' : 'Save this timetable'}
                </Button>
              </Box>
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ display: 'block', marginTop: 8 }}
              >
                Fixed routines (Snack, Lunch, Free Exploration…) are detected
                automatically and shown to teachers as non-plannable.
              </Typography>
            </>
          )}
        </>
      )}
    </Box>
  )
}

export default Timetables
